import { useMemo } from 'react';
import { SidebarProps } from '@trukt/ui/Sidebar';
import { useJobStatusGetQuery } from '@trukt/react-hooks/useJobStatusGetQuery';
import { useReportsInfiniteQuery } from '@trukt/react-hooks/use-reports-infinite-query';
import { CornerDownRight } from '@trukt/ui/icons';
import { generatePath } from '@shared/router';
import { PATH_NAMES } from '@shared/constants/pathNames';
import { JOB_LIST_PATH_NAMES } from '../../../../../../jobs/components/JobListView.pathnames';
import { useMemoItems } from './use-memo-items';

const JOB_STATUS_REFETCH_INTERVAL = 1000 * 60 * 2;

// NOTE: Removing type not, to break the TS. Let it infer.
export const useSidebarItems = () => {
  const jobStatusGetQuery = useJobStatusGetQuery({
    refetchInterval: JOB_STATUS_REFETCH_INTERVAL,
  });

  const jobReportQuery = useReportsInfiniteQuery('job');
  const orderReportQuery = useReportsInfiniteQuery('order');
  const accountingReportsQuery = useReportsInfiniteQuery('invoice');

  const jobReportItems = useMemoItems(jobReportQuery.data?.pages ?? [], PATH_NAMES.REPORT);
  const orderReportItems = useMemoItems(
    orderReportQuery.data?.pages ?? [],
    PATH_NAMES.admin.orders.REPORT,
  );
  const accountingReportsItems = useMemoItems(
    accountingReportsQuery.data?.pages ?? [],
    PATH_NAMES.accounting.REPORT,
  );

  const items = useMemo<SidebarProps['items']>(
    () => [
      {
        id: 'jobs',
        label: 'Jobs',
        items: [
          {
            id: 'all-jobs',
            label: 'All',
            to: JOB_LIST_PATH_NAMES.ALL,
          },
          {
            id: 'new-jobs',
            label: 'New',
            to: JOB_LIST_PATH_NAMES.NEW,
          },
          {
            id: 'tenders',
            label: 'Tenders',
            to: JOB_LIST_PATH_NAMES.TENDER,
            icon: <CornerDownRight className="size-4" />,

            count: jobStatusGetQuery.data?.tender ?? 0,
            classNameCount:
              jobStatusGetQuery.data && jobStatusGetQuery.data.tender > 0
                ? '!bg-primary-400'
                : undefined,
          },
          {
            id: 'on-hold',
            label: 'On Hold',
            to: JOB_LIST_PATH_NAMES.ON_HOLD,
          },
          {
            id: 'ready',
            label: 'Ready',
            to: JOB_LIST_PATH_NAMES.READY,
          },
          {
            id: 'in-progress',
            label: 'In Progress',
            to: JOB_LIST_PATH_NAMES.IN_PROGRESS,
          },
          {
            id: 'posted',
            label: 'Posted',
            to: JOB_LIST_PATH_NAMES.POSTED,
          },
          {
            id: 'requests',
            label: 'Requests',
            to: JOB_LIST_PATH_NAMES.REQUEST,
            icon: <CornerDownRight className="size-4" />,
            count: jobStatusGetQuery.data?.request ?? 0,
            classNameCount:
              jobStatusGetQuery.data && jobStatusGetQuery.data.request > 0
                ? '!bg-primary-400'
                : undefined,
          },
          {
            id: 'pending',
            label: 'Pending',
            to: JOB_LIST_PATH_NAMES.PENDING,
          },
          {
            id: 'declined',
            label: 'Declined',
            to: JOB_LIST_PATH_NAMES.DECLINED,
            icon: <CornerDownRight className="size-4" />,
            count: jobStatusGetQuery.data?.declined ?? 0,
            classNameCount:
              jobStatusGetQuery.data && jobStatusGetQuery.data.declined > 0
                ? '!bg-primary-400'
                : undefined,
          },
          {
            id: 'accepted',
            label: 'Dispatched',
            to: JOB_LIST_PATH_NAMES.DISPATCHED,
          },
          {
            id: 'picked-up',
            label: 'Picked Up',
            to: JOB_LIST_PATH_NAMES.PICKED_UP,
          },
          {
            id: 'delivered',
            label: 'Delivered',
            to: JOB_LIST_PATH_NAMES.DELIVERED,
          },
          {
            id: 'completed',
            label: 'Completed',
            to: JOB_LIST_PATH_NAMES.COMPLETED,
          },
          {
            id: 'canceled',
            label: 'Canceled',
            to: JOB_LIST_PATH_NAMES.CANCELED,
          },
          {
            id: 'deleted',
            label: 'Deleted',
            to: JOB_LIST_PATH_NAMES.DELETED,
          },
        ],
      },
      {
        id: 'accounting',
        label: 'Accounting',
        items: [
          {
            id: 'invoices',
            label: 'Invoices',
            to: generatePath(PATH_NAMES.accounting.LIST, { type: 'invoices' }),
          },
          {
            id: 'bills',
            label: 'Bills',
            to: generatePath(PATH_NAMES.accounting.LIST, { type: 'bills' }),
          },
          {
            id: 'finance-reports',
            label: 'Finance Reports',
            variant: 'infinitescroll' as const,
            items: accountingReportsItems,
            isLoadMoreActive: accountingReportsQuery.hasNextPage,
            onLoadMore: accountingReportsQuery.fetchNextPage,
          },
        ],
      },
      {
        id: 'job-reports',
        label: 'Reports',
        variant: 'infinitescroll' as const,
        items: jobReportItems,
        isLoadMoreActive: jobReportQuery.hasNextPage,
        onLoadMore: jobReportQuery.fetchNextPage,
      },
      {
        id: 'terminals',
        label: 'Terminals',
        items: [
          {
            id: 'terminal-locations',
            label: 'Locations',
            to: PATH_NAMES.terminal.locations.ROOT,
          },
          {
            id: 'terminal-assignments',
            label: 'Assignments',
            to: PATH_NAMES.terminal.assignments.LIST,
          },
          {
            id: 'terminal-lanes',
            label: 'Lanes',
            to: PATH_NAMES.terminal.lanes.LIST,
          },
        ],
      },
      {
        id: 'admin',
        label: 'Admin',
        items: [
          {
            id: 'admin-company',
            label: 'Company',
            items: [
              {
                id: 'app-settings',
                label: 'App Settings',
                to: PATH_NAMES.admin.company.ROOT,
              },
              {
                id: 'roles',
                label: 'Roles',
                to: PATH_NAMES.admin.company.ROLES,
              },
              {
                id: 'users',
                label: 'Users',
                to: PATH_NAMES.admin.company.users.ROOT,
              },
            ],
          },
          {
            id: 'clients',
            label: 'Clients',
            isFirstLevel: true,
            to: PATH_NAMES.admin.clients.ROOT,
          },
          {
            id: 'vendors',
            label: 'Vendors',
            to: PATH_NAMES.admin.vendors.ROOT,
            isFirstLevel: true,
          },
          {
            id: 'orders',
            label: 'Orders',
            items: [
              {
                id: 'all-orders',
                label: 'All Orders',
                to: PATH_NAMES.admin.orders.ROOT,
              },
              {
                id: 'order-reports',
                label: 'Order Reports',
                variant: 'infinitescroll' as const,
                items: orderReportItems,
                isLoadMoreActive: orderReportQuery.hasNextPage,
                onLoadMore: orderReportQuery.fetchNextPage,
              },
              {
                id: 'order-labels',
                label: 'Order Labels',
                to: PATH_NAMES.admin.orders.CASE_LABELS,
              },
            ],
          },
          {
            id: 'all-jobs',
            label: 'Job Labels',
            to: PATH_NAMES.admin.jobs.CASE_LABELS,
            isFirstLevel: true,
          },
          {
            id: 'lkq-fuel-rate',
            label: 'LKQ Fuel Rate',
            to: PATH_NAMES.admin.lkqFuelRate.ROOT,
            isFirstLevel: true,
          },
          {
            id: 'integrations',
            label: 'Integrations',
            items: [
              {
                id: 'integrations-configs',
                label: 'Configs',
                to: PATH_NAMES.admin.integrations.CONFIGS,
              },
              {
                id: 'integrations-companies',
                label: 'Companies',
                to: PATH_NAMES.admin.integrations.COMPANIES,
              },
              { id: 'integrations-apps', label: 'Apps', to: PATH_NAMES.admin.integrations.APPS },
            ],
          },
          {
            id: 'cargotTel',
            label: 'CargotTel',
            items: [
              { id: 'cargoTel-configs', label: 'Configs', to: PATH_NAMES.admin.cargoTel.CONFIGS },
            ],
          },
          {
            id: 'admin-loadboards',
            label: 'Load Boards',
            items: [
              {
                id: 'unsynced-posts',
                label: 'List Unsynced',
                to: PATH_NAMES.admin.loadboards.UNSYNCED,
              },
              {
                id: 'duplicate-lookup',
                label: 'Duplicate Lookup',
                to: PATH_NAMES.admin.loadboards.DUPLICATES,
              },
            ],
          },
          {
            id: 'admin-invoices',
            label: 'Invoices',
            items: [
              {
                id: 'invoice-sync',
                label: 'Sync',
                to: PATH_NAMES.admin.invoices.SYNC_VIN_AMOUNTS,
              },
            ],
          },
        ],
      },
      {
        id: 'bulk-import',
        label: 'Bulk Import',
        items: [
          {
            id: 'bulk-import-imports',
            label: 'Imports',
            items: [
              {
                id: 'bulk-import-imports-list',
                label: 'List',
                to: PATH_NAMES.bulkImport.imports.LIST,
              },
              {
                id: 'bulk-import-imports-create',
                label: 'Create',
                to: PATH_NAMES.bulkImport.imports.CREATE,
              },
            ],
          },
          {
            id: 'bulk-import-templates',
            label: 'Templates',
            items: [
              {
                id: 'bulk-import-templates-list',
                label: 'List',
                to: PATH_NAMES.bulkImport.templates.LIST,
              },
              {
                id: 'bulk-import-templates-create',
                label: 'Create',
                to: PATH_NAMES.bulkImport.templates.CREATE,
              },
            ],
          },
        ],
      },
    ],
    [
      accountingReportsItems,
      accountingReportsQuery.fetchNextPage,
      accountingReportsQuery.hasNextPage,
      jobReportItems,
      jobReportQuery.fetchNextPage,
      jobReportQuery.hasNextPage,
      jobStatusGetQuery.data,
      orderReportItems,
      orderReportQuery.fetchNextPage,
      orderReportQuery.hasNextPage,
    ],
  );

  return items;
};
